import { z } from "zod";
import { passwordSchema } from "@/features/identity/schemas/auth.schema";

/// RHF / Zod input schemas for the identity settings forms (rule F2).
/// Auth OTP flows intentionally keep their own ad-hoc state; this file
/// only covers create-workspace, invite-member, profile, account, and
/// workspace settings.

const SLUG_RE = /^[a-z0-9]*(?:-[a-z0-9]+)*$/;

export const createWorkspaceInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Give the workspace a name.")
    .max(120, "Keep it under 120 characters."),
  slug: z
    .string()
    .max(60, "Keep it under 60 characters.")
    .regex(SLUG_RE, "Lowercase letters, numbers, dashes only.")
    .optional()
    .or(z.literal("")),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceInputSchema>;

export const inviteMemberInputSchema = z.object({
  email: z.string().trim().min(1, "Email is required.").email("Enter a valid email address."),
  role: z.enum(["admin", "manager", "member"]),
});
export type InviteMemberInput = z.infer<typeof inviteMemberInputSchema>;

export const updateProfileInputSchema = z.object({
  firstName: z.string().max(100, "Keep it under 100 characters."),
  lastName: z.string().max(100, "Keep it under 100 characters."),
  nickName: z.string().max(50, "Keep it under 50 characters."),
  displayName: z
    .string()
    .trim()
    .min(1, "Give yourself a display name.")
    .max(100, "Keep it under 100 characters."),
  timezone: z.string(),
  preferredCurrency: z.string(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;

/**
 * Change-password form in the profile Security card. `currentPassword` is
 * optional at the schema level so password-less users (Google / magic-link
 * only) can set an initial password, the form decides whether to show the
 * field based on `hasPassword`. The shore enforces the current-password check
 * server-side regardless.
 */
export const changePasswordInputSchema = z
  .object({
    currentPassword: z.string(),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;

export const updateAccountInputSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(1, "Account needs a name.")
    .max(120, "Keep it under 120 characters."),
});
export type UpdateAccountInput = z.infer<typeof updateAccountInputSchema>;

export const updateWorkspaceInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Workspace needs a name.")
    .max(120, "Keep it under 120 characters."),
  slug: z
    .string()
    .min(1, "Slug is required.")
    .max(60, "Keep it under 60 characters.")
    .regex(SLUG_RE, "Lowercase letters, numbers, dashes only."),
  operatingDate: z.string().refine((value) => value === "" || /^\d{4}-\d{2}-\d{2}$/.test(value), {
    message: "Use a real calendar date.",
  }),
});
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceInputSchema>;
